/* eslint-disable function-paren-newline */
/* eslint-disable max-len */
/* eslint-disable react/require-default-props */
/* eslint-disable implicit-arrow-linebreak */

/* eslint-disable react/destructuring-assignment */
import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypedSelector';

import { useMutation } from '@tanstack/react-query';
import mfaService from 'src/services/mfa.service';
import { IValidateUserSignin } from 'src/interfaces';
import { AlertAdapter, FormErrorWrap, RegularButton } from 'pg-ui-library';
import signinService from 'src/services/signin.service';
import { AuthContext, IAuthContext } from '../../auth.context';
import { Container, FooterContainer, FooterLink, FormContainter, InfoText, MainContainer } from './styles';
import MFAInput from '../mfa-input';

export type MFAMethodSwitcherProps = {
  radioGroupName: string;
  expandedMethod: 'mfa' | 'email' | null;
  onSelectMethod: (method: 'mfa' | 'email') => void; // eslint-disable-line no-unused-vars -- TS callback type only
  disabled?: boolean;
};

export const MFAForm: React.FC<{
  mode: 'mfa' | 'email';
  methodSwitcher?: MFAMethodSwitcherProps;
}> = ({ mode, methodSwitcher }) => {
  const { signin, signinWithEmailCode } = useActions();
  const { error } = useTypedSelector(state => state.auth);
  const { t } = useTranslation();
  const [mfaSubmitted, setMfaSubmitted] = useState(false);

  const validationSchema = yup.object().shape({
    code: yup.string().required(t('global.form.required')).max(255),
  });

  const { username, password } = useContext(AuthContext) as IAuthContext;

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema,
    onSubmit: async values => {
      const name = username as string;
      const pass = password as string;
      setMfaSubmitted(true);
      if (mode === 'email') {
        signinWithEmailCode({ username: name, password: pass, emailCode: values.code });
      } else {
        signin({ username: name, password: pass, mfaCode: values.code });
      }
    },
  });

  useEffect(() => {
    if (mfaSubmitted && error) {
      const isLocked = error.toLowerCase().includes('locked');
      let errorMessage = mode === 'email' ? 'Invalid email confirmation code' : t('mfa.invalid');
      if (isLocked) {
        errorMessage = 'Account temporarily locked due to too many failed MFA attempts. Please try again in 1 hour.';
      }
      AlertAdapter(errorMessage, 'error');
    }
  }, [error, mfaSubmitted, mode]);

  const mfaMutation = useMutation((variables: IValidateUserSignin) => mfaService.generateNewQRCode(variables));

  const generateNewQRCode = async () => {
    try {
      const name = username as string;
      const pass = password as string;
      await mfaMutation.mutateAsync({
        username: name,
        password: pass,
      });
      AlertAdapter(`An email with the QR Code was sent to ${name}`, 'success');
    } catch (e: any) {
      AlertAdapter(
        e?.response?.status === 429
          ? 'Account temporarily locked due to too many failed MFA attempts. Please try again in 1 hour.'
          : t('mfa.generateError'),
        'error'
      );
    }
  };

  const resendEmailCode = async () => {
    const name = username as string;
    const pass = password as string;
    try {
      const response = await signinService.requestEmailCode({
        username: name,
        password: pass,
      });
      const destinationEmail = response?.data?.destinationEmail || name;
      AlertAdapter(`A confirmation code was sent to ${destinationEmail}`, 'success');
    } catch (e: any) {
      AlertAdapter(
        e?.response?.status === 429
          ? 'Account temporarily locked due to too many failed MFA attempts. Please try again in 1 hour.'
          : 'Unable to send confirmation code by email',
        'error'
      );
    }
  };

  return (
    <MainContainer>
      <Container onSubmit={formik.handleSubmit}>
        <InfoText>
          {mode === 'email' ? 'Enter the confirmation code sent to your email address to continue.' : t('mfa.helpText')}
        </InfoText>
        <FormContainter>
          <MFAInput
            label={mode === 'email' ? 'Email confirmation code' : t('mfa.inputLabel')}
            placeholder={mode === 'email' ? 'Insert the code sent by email' : 'Insert your MFA code'}
            id="code"
            name="code"
            onChange={formik.handleChange}
            value={formik.values.code}
          />
          {methodSwitcher && mode === 'email' && (
            <label
              htmlFor="mfa-form-switch-to-authenticator"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                cursor: methodSwitcher.disabled ? 'default' : 'pointer',
                fontSize: '14px',
                color: '#374151',
              }}
            >
              <input
                id="mfa-form-switch-to-authenticator"
                type="radio"
                name={methodSwitcher.radioGroupName}
                value="mfa"
                checked={methodSwitcher.expandedMethod === 'mfa'}
                disabled={methodSwitcher.disabled}
                onChange={() => methodSwitcher.onSelectMethod('mfa')}
                style={{ accentColor: '#4948ac', width: '18px', height: '18px', flexShrink: 0 }}
              />
              <span>Use authenticator app method</span>
            </label>
          )}
          {methodSwitcher && mode === 'mfa' && (
            <label
              htmlFor="mfa-form-switch-to-email"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                cursor: methodSwitcher.disabled ? 'default' : 'pointer',
                fontSize: '14px',
                color: '#374151',
              }}
            >
              <input
                id="mfa-form-switch-to-email"
                type="radio"
                name={methodSwitcher.radioGroupName}
                value="email"
                checked={methodSwitcher.expandedMethod === 'email'}
                disabled={methodSwitcher.disabled}
                onChange={() => methodSwitcher.onSelectMethod('email')}
                style={{ accentColor: '#4948ac', width: '18px', height: '18px', flexShrink: 0 }}
              />
              <span>Use email confirmation code</span>
            </label>
          )}
          <FormErrorWrap
            visible={(formik.errors.code && formik.touched.code) || false}
            errorMessage={formik.errors.code || ''}
          />
          <RegularButton
            style={{ width: '100%', textTransform: 'uppercase', borderRadius: '10px', minHeight: '40px' }}
            type="submit"
            disabled={!formik.isValid}
          >
            {t('login.form.button')}
          </RegularButton>
          {mode === 'email' && (
            <FooterContainer onClick={resendEmailCode}>
              <FooterLink>Resend confirmation code</FooterLink>
            </FooterContainer>
          )}
          {mode === 'mfa' && (
            <FooterContainer onClick={generateNewQRCode}>
              <FooterLink>{t('mfa.generate')}</FooterLink>
            </FooterContainer>
          )}
        </FormContainter>
      </Container>
    </MainContainer>
  );
};
